import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import './Collaborations.css';
import AudienceEditModal from './AudienceEditModal';
import EmailSettingsModal from './EmailSettingsModal';

// Country flag helper function
const getCountryFlag = (countryCode: string): string => {
  const code = countryCode?.toLowerCase();
  const flagMap: { [key: string]: string } = {
    'us': '🇺🇸', 'gb': '🇬🇧', 'uk': '🇬🇧', 'ca': '🇨🇦', 'au': '🇦🇺',
    'de': '🇩🇪', 'fr': '🇫🇷', 'it': '🇮🇹', 'es': '🇪🇸', 'nl': '🇳🇱',
    'se': '🇸🇪', 'no': '🇳🇴', 'dk': '🇩🇰', 'fi': '🇫🇮', 'pl': '🇵🇱',
    'ru': '🇷🇺', 'cn': '🇨🇳', 'jp': '🇯🇵', 'kr': '🇰🇷', 'in': '🇮🇳',
    'br': '🇧🇷', 'mx': '🇲🇽', 'ar': '🇦🇷', 'za': '🇿🇦', 'eg': '🇪🇬',
    'sa': '🇸🇦', 'ae': '🇦🇪', 'il': '🇮🇱', 'tr': '🇹🇷', 'ch': '🇨🇭',
    'at': '🇦🇹', 'be': '🇧🇪', 'pt': '🇵🇹', 'gr': '🇬🇷', 'ie': '🇮🇪',
    'nz': '🇳🇿', 'sg': '🇸🇬', 'hk': '🇭🇰', 'my': '🇲🇾', 'th': '🇹🇭',
    'ph': '🇵🇭', 'id': '🇮🇩', 'vn': '🇻🇳', 'tw': '🇹🇼', 'pk': '🇵🇰',
    'bd': '🇧🇩', 'ng': '🇳🇬', 'ke': '🇰🇪', 'et': '🇪🇹', 'gh': '🇬🇭',
    'ug': '🇺🇬', 'tz': '🇹🇿', 'ma': '🇲🇦', 'dz': '🇩🇿', 'tn': '🇹🇳',
    'ly': '🇱🇾', 'cl': '🇨🇱', 'pe': '🇵🇪', 'co': '🇨🇴', 've': '🇻🇪',
    'ec': '🇪🇨', 'uy': '🇺🇾', 'py': '🇵🇾', 'bo': '🇧🇴', 'cr': '🇨🇷',
    'pa': '🇵🇦', 'do': '🇩🇴', 'gt': '🇬🇹', 'sv': '🇸🇻', 'hn': '🇭🇳',
    'ni': '🇳🇮', 'cu': '🇨🇺', 'jm': '🇯🇲', 'ht': '🇭🇹', 'lu': '🇱🇺',
    'cy': '🇨🇾', 'mt': '🇲🇹', 'is': '🇮🇸', 'cz': '🇨🇿', 'sk': '🇸🇰',
    'hu': '🇭🇺', 'si': '🇸🇮', 'hr': '🇭🇷', 'ba': '🇧🇦', 'rs': '🇷🇸',
    'me': '🇲🇪', 'mk': '🇲🇰', 'al': '🇦🇱', 'bg': '🇧🇬', 'ro': '🇷🇴',
    'md': '🇲🇩', 'ua': '🇺🇦', 'by': '🇧🇾', 'lt': '🇱🇹', 'lv': '🇱🇻',
    'ee': '🇪🇪'
  };
  return flagMap[code] || '🏳️';
};

interface PostMetrics {
  primaryPlatform: 'tiktok' | 'instagram' | null;
  tiktokViews?: number;
  instagramViews?: number;
  primaryViews: number;
  primaryPostedAt: string | null;
  cpm: number | null;
  engagementRate?: number; // 해당 게시글의 engagement rate
  lastFetched: string;
}

interface Collaboration {
  _id: string;
  responseId: any;
  email: string;
  platform: string;
  handle: string;
  agreedPrice: number;
  videoTopic?: string;
  collaborationStage: string;
  sourceType: string;
  paused?: boolean;
  mentions?: number;
  mentionsLastUpdated?: string;
  
  // Script fields
  scriptDraft?: string;
  scriptSubmittedAt?: string;
  scriptApproved?: boolean;
  scriptApprovedAt?: string;
  scriptFeedback?: string;
  
  // Video fields
  videoFile?: string;
  videoSubmittedAt?: string;
  videoApproved?: boolean;
  videoApprovedAt?: string;
  videoFeedback?: string;
  
  // Post fields
  postUrlInstagram?: string;
  postUrlTikTok?: string;
  postUrlYoutube?: string;
  postSubmittedAt?: string;
  postVerified?: boolean;
  postVerifiedAt?: string;
  postMetrics?: PostMetrics;
  
  // Payment fields
  paymentStatus?: string;
  paymentProcessed?: boolean;
  paymentAmount?: number;
  paymentDate?: string;
  paymentMethod?: string;
  invoiceLink?: string;
  
  // Personal & Tax info
  personalInfo?: {
    firstName?: string;
    lastName?: string;
    country?: string;
    submittedAt?: string;
  };
  taxInfo?: {
    type?: string;
    taxId?: string;
    submittedAt?: string;
    verified?: boolean;
  };
  
  // Deadline & Email
  deadline?: string;
  lastReminderSent?: string;
  emailSent?: boolean;

  // Stripe 쿠폰 정보
  stripeCoupon?: {
    couponId: string;
    couponCode: string;
    percentOff: number;
    redeemed: number;
    createdAt: string;
  };

  // Metrics (migrated from CSV)
  totalViews?: number;
  recentViews?: number;
  cpm?: number;
  maxCPM?: number;
  rpm?: number;
  cpmDelta?: number;
  sumChange?: number;
  countChange?: number;
  
  // Audience data
  audienceData?: any;
  primaryCountry?: string;
  language?: string;
  
  // Affiliate info
  affiliateStatus?: string;
  affiliateCoupon?: string;
  affiliateEmail?: string;
  
  // Other
  isLongTerm?: boolean;
  notes?: string;
  notes1?: string;
  notes2?: string;

  createdAt: string;
  updatedAt: string;
}

interface StageInfo {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
  progress: number;
}

// Column configuration interface
interface ColumnConfig {
  key: string;
  label: string;
  width?: number;
  visible: boolean;
  sortable?: boolean;
}

const Collaborations: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();

  // URL 파라미터에서 초기값 가져오기
  const getInitialFilter = () => searchParams.get('filter') || 'all';
  const getInitialSearch = () => searchParams.get('search') || '';
  const getInitialSort = () => searchParams.get('sort') || 'recent';
  const getInitialOrder = () => (searchParams.get('order') as 'asc' | 'desc') || 'desc';

  const [collaborations, setCollaborations] = useState<Collaboration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollab, setSelectedCollab] = useState<Collaboration | null>(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackFiles, setFeedbackFiles] = useState<File[]>([]);
  const [filter, setFilter] = useState(getInitialFilter());
  const [searchTerm, setSearchTerm] = useState(getInitialSearch());
  const [sortBy, setSortBy] = useState(getInitialSort());
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(getInitialOrder());
  const [stats, setStats] = useState<any[]>([]);
  const [isUpdatingStatuses, setIsUpdatingStatuses] = useState(false);
  const [updateResult, setUpdateResult] = useState<any>(null);
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  const [fetchingMetrics, setFetchingMetrics] = useState<{ [key: string]: boolean }>({});
  const [downloadingVideo, setDownloadingVideo] = useState<{ [key: string]: boolean }>({});
  const [sendingReminder, setSendingReminder] = useState<{ [key: string]: boolean }>({});
  const [sendingAllReminders, setSendingAllReminders] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showMetricsDropdown, setShowMetricsDropdown] = useState(false);
  const [fetchingBulkMetrics, setFetchingBulkMetrics] = useState(false);
  const [reminderTargets, setReminderTargets] = useState<{
    script: Collaboration[];
    video: Collaboration[];
    post: Collaboration[];
  }>({ script: [], video: [], post: [] });
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedCollabForBatch, setSelectedCollabForBatch] = useState<Collaboration | null>(null);
  const [batchSize, setBatchSize] = useState(4);
  const [batchPrice, setBatchPrice] = useState(0);
  const [batchPricingType, setBatchPricingType] = useState<'fixed' | 'rpm'>('fixed');
  const [batchRpmRate, setBatchRpmRate] = useState(0);
  const [batchMaxPrice, setBatchMaxPrice] = useState(0);
  const [batchMessage, setBatchMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [useExistingBatch, setUseExistingBatch] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');
  const [existingBatches, setExistingBatches] = useState<any[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    handle: '',
    postUrlInstagram: '',
    postUrlTikTok: '',
    agreedPrice: '',
    invoiceLink: '',
    videoTopic: ''
  });
  const [isCreating, setIsCreating] = useState(false);

  // Audience edit modal state
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [selectedCollabForAudience, setSelectedCollabForAudience] = useState<Collaboration | null>(null);

  // Multi-select state
  const [selectedCollaborations, setSelectedCollaborations] = useState<Set<string>>(new Set());
  const [selectAllCollaborations, setSelectAllCollaborations] = useState(false);

  // Inline editing state for videoTopic
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicValue, setEditingTopicValue] = useState<string>('');

  // Batch creators set
  const [batchCreators, setBatchCreators] = useState<Set<string>>(new Set());

  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesContent, setNotesContent] = useState('');
  const [selectedCollabForNotes, setSelectedCollabForNotes] = useState<Collaboration | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);

  // Email settings modal state
  const [showEmailSettingsModal, setShowEmailSettingsModal] = useState(false);

  // Define all available columns
  const defaultColumns: ColumnConfig[] = [
    { key: 'handle', label: 'Creator', visible: true, sortable: true },
    { key: 'email', label: 'Email', visible: false, sortable: true },
    { key: 'platform', label: 'Response Platform', visible: true, sortable: true },
    { key: 'agreedPrice', label: 'Price', visible: true, sortable: true },
    { key: 'videoTopic', label: 'Topic', visible: true, sortable: true },
    { key: 'mentions', label: 'Mentions', visible: true, sortable: true },
    { key: 'postUrlTikTok', label: 'TikTok Post', visible: true, sortable: false },
    { key: 'postUrlInstagram', label: 'IG Post', visible: true, sortable: false },
    { key: 'postUrlYoutube', label: 'YT Post', visible: false, sortable: false },
    { key: 'primaryViews', label: 'Views', visible: true, sortable: true },
    { key: 'totalViews', label: 'Total Views', visible: true, sortable: true },
    { key: 'tiktokViews', label: 'TT Views', visible: false, sortable: true },
    { key: 'instagramViews', label: 'IG Views', visible: false, sortable: true },
    { key: 'postEngagementRate', label: 'Engage', visible: true, sortable: true },
    { key: 'cpm', label: 'CPM', visible: true, sortable: true },
    { key: 'maxCPM', label: 'Max CPM', visible: true, sortable: true },
    { key: 'cpmDelta', label: 'CPM Δ', visible: true, sortable: true },
    { key: 'sumChange', label: 'Revenue Impact %', visible: true, sortable: true },
    { key: 'audienceData', label: 'Audience', visible: true, sortable: false },
    { key: 'primaryPostedAt', label: 'Posted', visible: true, sortable: true },
    { key: 'coupon', label: 'Coupon', visible: true, sortable: false },
    { key: 'redeemed', label: 'Redeemed', visible: true, sortable: true },
    { key: 'primaryPlatform', label: 'Primary Platform', visible: false, sortable: true },
    { key: 'collaborationStage', label: 'Stage', visible: true, sortable: true },
    { key: 'paymentStatus', label: 'Payment', visible: false, sortable: true },
    { key: 'paymentAmount', label: 'Paid', visible: false, sortable: true },
    { key: 'deadline', label: 'Deadline', visible: false, sortable: true },
    { key: 'scriptApproved', label: 'Script', visible: false, sortable: false },
    { key: 'videoApproved', label: 'Video', visible: false, sortable: false },
    { key: 'postVerified', label: 'Post', visible: false, sortable: false },
    { key: 'primaryCountry', label: 'Country', visible: false, sortable: true },
    { key: 'language', label: 'Language', visible: false, sortable: true },
    { key: 'affiliateStatus', label: 'Affiliate', visible: false, sortable: true },
    { key: 'isLongTerm', label: 'Long Term', visible: false, sortable: false },
    { key: 'emailSent', label: 'Email Sent', visible: false, sortable: false },
    { key: 'notes', label: 'Notes', visible: true, sortable: false },
    { key: 'createdAt', label: 'Created', visible: false, sortable: true },
    { key: 'actions', label: 'Actions', visible: true, sortable: false }
  ];
  
  // Load column settings from localStorage or use defaults
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('collaborations-columns-v11'); // v11로 변경하여 videoTopic 칼럼 추가된 새로운 설정 적용
    if (saved) {
      try {
        const savedColumns = JSON.parse(saved);
        // 기존 설정에서 라벨 업데이트
        return savedColumns.map((col: ColumnConfig) => {
          if (col.key === 'platform') {
            return { ...col, label: 'Response Platform' };
          }
          if (col.key === 'primaryPlatform') {
            return { ...col, label: 'Primary Platform' };
          }
          return col;
        });
      } catch {
        return defaultColumns;
      }
    }
    return defaultColumns;
  });

  // URL 파라미터 업데이트 함수
  const updateURLParams = useCallback((params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '' && value !== 'recent' && value !== 'desc') {
        newSearchParams.set(key, value);
      } else if ((key === 'filter' && value === 'all') ||
                 (key === 'sort' && value === 'recent') ||
                 (key === 'order' && value === 'desc') ||
                 (key === 'search' && value === '')) {
        newSearchParams.delete(key);
      } else if (value) {
        newSearchParams.set(key, value);
      }
    });

    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // URL 파라미터가 변경될 때 상태 동기화 (브라우저 뒤로가기/앞으로가기 지원)
  useEffect(() => {
    const urlFilter = searchParams.get('filter') || 'all';
    const urlSearch = searchParams.get('search') || '';
    const urlSort = searchParams.get('sort') || 'recent';
    const urlOrder = (searchParams.get('order') as 'asc' | 'desc') || 'desc';

    setFilter(urlFilter);
    setSearchTerm(urlSearch);
    setSortBy(urlSort);
    setSortOrder(urlOrder);
  }, [location.search]);

  useEffect(() => {
    fetchCollaborations();
    fetchBatchCreators();
  }, [filter, sortBy, sortOrder]);

  // 필터 변경 핸들러
  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    updateURLParams({
      filter: newFilter,
      search: searchTerm,
      sort: sortBy,
      order: sortOrder
    });
  };

  // 검색어 변경 핸들러 (디바운싱 적용)
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);
  const handleSearchChange = (newSearch: string) => {
    setSearchTerm(newSearch);

    // 이전 타이머 정리
    if (searchTimer) clearTimeout(searchTimer);

    // 새 타이머 설정
    const timer = setTimeout(() => {
      updateURLParams({
        filter: filter,
        search: newSearch,
        sort: sortBy,
        order: sortOrder
      });
    }, 300);

    setSearchTimer(timer);
  };
  
  // Fetch batch creators
  const fetchBatchCreators = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batch`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const result = await response.json();
      if (result.success) {
        const creatorsSet = new Set<string>();
        result.data.forEach((batch: any) => {
          if (batch.handle) {
            // Remove @ symbol if present and convert to lowercase for matching
            const cleanHandle = batch.handle.replace('@', '').toLowerCase();
            creatorsSet.add(cleanHandle);
          }
        });
        setBatchCreators(creatorsSet);
      }
    } catch (error) {
      console.error('Error fetching batch creators:', error);
    }
  };
  
  // Save column settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('collaborations-columns-v11', JSON.stringify(columns)); // v11로 변경
  }, [columns]);

  // Toggle column visibility
  const toggleColumnVisibility = (key: string) => {
    setColumns(prev => prev.map(col => 
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };
  
  // Handle drag start
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  
  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverIndex(null);
  };
  
  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedColumn === null || draggedColumn === dropIndex) {
      setDraggedColumn(null);
      setDragOverIndex(null);
      return;
    }
    
    const newColumns = [...columns];
    const draggedItem = newColumns[draggedColumn];
    
    // Remove dragged item
    newColumns.splice(draggedColumn, 1);
    
    // Insert at new position
    const adjustedDropIndex = draggedColumn < dropIndex ? dropIndex - 1 : dropIndex;
    newColumns.splice(adjustedDropIndex, 0, draggedItem);
    
    setColumns(newColumns);
    setDraggedColumn(null);
    setDragOverIndex(null);
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverIndex(null);
  };
  
  // Handle column sorting
  const handleSort = (key: string) => {
    if (!columns.find(col => col.key === key)?.sortable) return;

    let newOrder: 'asc' | 'desc' = 'desc';

    if (sortBy === key) {
      // 현재 정렬 중인 칼럼을 다시 클릭하면 오름차순/내림차순 토글
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
    } else {
      // 새로운 칼럼을 클릭할 때
      setSortBy(key);
      // 수치 관련 컬럼들과 날짜 컬럼들은 첫 클릭시 내림차순(높은 값부터/최신 날짜부터)이 더 유용
      const numericalColumns = [
        'cpmDelta', 'cpm', 'maxCPM', 'sumChange', 'primaryViews', 'totalViews',
        'tiktokViews', 'instagramViews', 'agreedPrice', 'paymentAmount', 'mentions',
        'primaryPostedAt', 'posted', 'deadline', 'createdAt', 'updatedAt'
      ];
      newOrder = numericalColumns.includes(key) ? 'desc' : 'asc';
      setSortOrder(newOrder);
    }

    // URL 파라미터 업데이트
    updateURLParams({
      filter: filter,
      search: searchTerm,
      sort: key,
      order: newOrder
    });
  };
  
  const fetchCollaborations = async (): Promise<Collaboration[] | null> => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('stage', filter);
      }
      // 더 많은 데이터를 가져오기 위해 limit 증가
      params.append('limit', '1000');  // 또는 원하는 개수
      
      console.log('Current filter:', filter);
      console.log('API URL:', `${API_URL}/api/collaborations/dashboard?${params}`);
      
      const response = await fetch(`${API_URL}/api/collaborations/dashboard?${params}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      
      console.log('API Response:', data);
      console.log('Fetched collaborations count:', data.data?.length);
      console.log('Stats:', data.stats);
      
      if (data.success) {
        let sortedData = [...data.data];
        
        // Apply sorting based on column key
        sortedData.sort((a, b) => {
          let aVal, bVal;
          
          switch(sortBy) {
            case 'handle':
              aVal = a.handle || '';
              bVal = b.handle || '';
              break;
            case 'email':
              aVal = a.email || '';
              bVal = b.email || '';
              break;
            case 'platform':
              aVal = a.platform || '';
              bVal = b.platform || '';
              break;
            case 'agreedPrice':
              aVal = a.agreedPrice || 0;
              bVal = b.agreedPrice || 0;
              break;
            case 'videoTopic':
              aVal = a.videoTopic || '';
              bVal = b.videoTopic || '';
              break;
            case 'mentions':
              aVal = a.mentions || 0;
              bVal = b.mentions || 0;
              break;
            case 'paymentStatus':
              aVal = a.paymentStatus || '';
              bVal = b.paymentStatus || '';
              break;
            case 'paymentAmount':
              aVal = a.paymentAmount || 0;
              bVal = b.paymentAmount || 0;
              break;
            case 'views':
            case 'primaryViews':
              aVal = a.postMetrics?.primaryViews || 0;
              bVal = b.postMetrics?.primaryViews || 0;
              break;
            case 'tiktokViews':
              aVal = a.postMetrics?.tiktokViews || 0;
              bVal = b.postMetrics?.tiktokViews || 0;
              break;
            case 'instagramViews':
              aVal = a.postMetrics?.instagramViews || 0;
              bVal = b.postMetrics?.instagramViews || 0;
              break;
            case 'postEngagementRate':
              aVal = a.postMetrics?.engagementRate || 0;
              bVal = b.postMetrics?.engagementRate || 0;
              break;
            case 'maxCPM':
              aVal = a.maxCPM || 0;
              bVal = b.maxCPM || 0;
              break;
            case 'cpmDelta':
              aVal = a.cpmDelta || 0;
              bVal = b.cpmDelta || 0;
              break;
            case 'sumChange':
              aVal = a.sumChange || 0;
              bVal = b.sumChange || 0;
              break;
            case 'primaryPostedAt':
            case 'posted':
              aVal = a.postMetrics?.primaryPostedAt ? new Date(a.postMetrics.primaryPostedAt).getTime() : 0;
              bVal = b.postMetrics?.primaryPostedAt ? new Date(b.postMetrics.primaryPostedAt).getTime() : 0;
              break;
            case 'primaryPlatform':
              aVal = a.postMetrics?.primaryPlatform || '';
              bVal = b.postMetrics?.primaryPlatform || '';
              break;
            case 'redeemed':
              aVal = a.stripeCoupon?.redeemed || 0;
              bVal = b.stripeCoupon?.redeemed || 0;
              break;
            case 'cpm':
              aVal = a.cpm || 999999;
              bVal = b.cpm || 999999;
              break;
            case 'totalViews':
              aVal = a.totalViews || 0;
              bVal = b.totalViews || 0;
              break;
            case 'deadline':
              aVal = a.deadline ? new Date(a.deadline).getTime() : 0;
              bVal = b.deadline ? new Date(b.deadline).getTime() : 0;
              break;
            case 'createdAt':
              aVal = new Date(a.createdAt).getTime();
              bVal = new Date(b.createdAt).getTime();
              break;
            case 'updatedAt':
              aVal = new Date(a.updatedAt).getTime();
              bVal = new Date(b.updatedAt).getTime();
              break;
            case 'recent': // Keep backward compatibility
              aVal = new Date(a.updatedAt).getTime();
              bVal = new Date(b.updatedAt).getTime();
              break;
            default:
              aVal = (a as any)[sortBy] || '';
              bVal = (b as any)[sortBy] || '';
          }
          
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortOrder === 'asc' 
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          } else {
            return sortOrder === 'asc'
              ? (aVal as number) - (bVal as number)
              : (bVal as number) - (aVal as number);
          }
        });
        
        setCollaborations(sortedData);
        setStats(data.stats || []);
        return sortedData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching collaborations:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingBatches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batch`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const result = await response.json();
      if (result.success) {
        setExistingBatches(result.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const openBatchModal = async (collab: Collaboration) => {
    setSelectedCollabForBatch(collab);
    const defaultPrice = Math.round(collab.agreedPrice * 0.8); // 20% discount default
    setBatchPrice(defaultPrice);
    setBatchSize(4);
    setBatchMessage('');
    setShowAdvanced(false);
    setUseExistingBatch(false);
    setSelectedBatchId('');
    setShowBatchModal(true);
    // Fetch existing batches when modal opens
    await fetchExistingBatches();
  };

  const handleDiscountSelect = (discount: number) => {
    if (selectedCollabForBatch) {
      const discountedPrice = Math.round(selectedCollabForBatch.agreedPrice * (1 - discount / 100));
      setBatchPrice(discountedPrice);
    }
  };


  const createNewCollaboration = async () => {
    if (!createFormData.handle || !createFormData.agreedPrice) {
      alert('Handle and Price are required fields');
      return;
    }

    try {
      setIsCreating(true);
      
      const response = await fetch(`${API_URL}/api/collaborations/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          handle: createFormData.handle,
          postUrlInstagram: createFormData.postUrlInstagram,
          postUrlTikTok: createFormData.postUrlTikTok,
          agreedPrice: parseFloat(createFormData.agreedPrice),
          invoiceLink: createFormData.invoiceLink,
          videoTopic: createFormData.videoTopic
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Collaboration created successfully!');
        setShowCreateModal(false);
        setCreateFormData({
          handle: '',
          postUrlInstagram: '',
          postUrlTikTok: '',
          agreedPrice: '',
          invoiceLink: '',
          videoTopic: ''
        });
        // Refresh the collaborations list
        await fetchCollaborations();
      } else {
        alert(result.error || 'Failed to create collaboration');
      }
    } catch (error) {
      console.error('Error creating collaboration:', error);
      alert('Failed to create collaboration');
    } finally {
      setIsCreating(false);
    }
  };

  const createBatch = async () => {
    if (!selectedCollabForBatch) return;
    
    try {
      let response;
      
      if (useExistingBatch && selectedBatchId) {
        // Assign to existing batch
        response = await fetch(`${API_URL}/api/batch/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            collaborationId: selectedCollabForBatch._id,
            batchId: selectedBatchId
          })
        });
      } else {
        // Create new batch
        const batchData: any = {
          batchSize,
          pricingType: batchPricingType,
          message: batchMessage || undefined
        };

        if (batchPricingType === 'fixed') {
          batchData.batchPrice = batchPrice;
        } else {
          batchData.rpmRate = batchRpmRate;
          batchData.maxPrice = batchMaxPrice;
        }

        response = await fetch(`${API_URL}/api/batch/create/${selectedCollabForBatch._id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(batchData)
        });
      }
      
      const result = await response.json();
      
      if (result.success) {
        alert(result.message || (useExistingBatch ? 'Assigned to batch successfully!' : 'Batch created successfully!'));
        setShowBatchModal(false);
        setSelectedCollabForBatch(null);
      } else {
        alert(result.error || 'Failed to create batch');
      }
    } catch (error) {
      console.error('Error creating batch:', error);
      alert('Failed to create batch');
    }
  };

  const handleApprove = async (collabId: string, type: 'script' | 'video') => {
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collabId}/${type}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          approved: true,
          feedback: feedback || 'Approved!'
        }),
      });

      if (response.ok) {
        fetchCollaborations();
        setSelectedCollab(null);
        setFeedback('');
        setFeedbackFiles([]);
      }
    } catch (error) {
      console.error('Error approving:', error);
    }
  };

  const fetchPostMetrics = async (collabId: string) => {
    try {
      setFetchingMetrics(prev => ({ ...prev, [collabId]: true }));
      
      const response = await fetch(`${API_URL}/api/collaborations/${collabId}/fetch-metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
      });

      const data = await response.json();
      console.log('📦 Fetch metrics response:', data);
      
      if (data.success) {
        console.log('📊 Metrics updated:', data.data);
        // Refresh the collaborations list to show updated data
        await fetchCollaborations();
        const totalViews = data.data.collaboration?.totalViews || 0;
        const cpm = data.data.collaboration?.cpm || 0;
        
        let message = `Post metrics fetched successfully!\n\nTotal Views: ${totalViews.toLocaleString()}`;
        
        if (cpm > 0) {
          message += `\nCPM: $${cpm.toFixed(2)}`;
        } else if (totalViews > 0) {
          message += `\nCPM: $0.00 (Price not set)`;
        } else {
          message += `\nCPM: N/A`;
        }
        
        alert(message);
      } else {
        alert(`Failed to fetch metrics: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching post metrics:', error);
      alert('Failed to fetch post metrics');
    } finally {
      setFetchingMetrics(prev => ({ ...prev, [collabId]: false }));
    }
  };

  const downloadVideo = async (collaboration: Collaboration) => {
    try {
      // 다운로드 시작 - 상태 설정
      setDownloadingVideo(prev => ({ ...prev, [collaboration._id]: true }));

      console.log('📥 Starting video download for:', collaboration.handle);

      // 사용 가능한 URL 찾기 (videoFile이 있든 없든 백엔드 API를 통해 처리)
      const availableUrl = collaboration.postUrlTikTok || collaboration.postUrlInstagram;

      // videoFile이 있거나 post URL이 있으면 백엔드 API 호출
      if (collaboration.videoFile || availableUrl) {
        const platform = collaboration.postUrlTikTok ? 'tiktok' : 'instagram';
        console.log(`📱 Platform: ${platform}, has videoFile: ${!!collaboration.videoFile}`);

        // 백엔드 API 호출 (백엔드에서 videoFile 우선 처리)
        const response = await fetch(`${API_URL}/api/collaborations/${collaboration._id}/download-video`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            platform,
            postUrl: availableUrl || ''
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // 파일 다운로드 처리
        const blob = await response.blob();

        // 파일명 생성 로직
        let filename = 'video.mp4';
        const cleanHandle = collaboration.handle.replace('@', '');

        if (collaboration.postUrlInstagram) {
          const instagramMatch = collaboration.postUrlInstagram.match(/\/(p|reel)\/([^\/\?]+)/);
          if (instagramMatch) {
            filename = `${cleanHandle}_instagram_${instagramMatch[2]}.mp4`;
          } else {
            filename = `${cleanHandle}_instagram_video.mp4`;
          }
        } else if (collaboration.postUrlTikTok) {
          const tiktokMatch = collaboration.postUrlTikTok.match(/\/video\/(\d+)/);
          if (tiktokMatch) {
            filename = `${cleanHandle}_tiktok_${tiktokMatch[1]}.mp4`;
          } else {
            filename = `${cleanHandle}_tiktok_video.mp4`;
          }
        } else {
          filename = `${cleanHandle}_video.mp4`;
        }

        // 브라우저에서 파일 다운로드
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log('✅ Video download completed:', filename);
        toast.success(`Video downloaded successfully: ${filename}`);

      } else {
        alert('No video file or post URL found for this collaboration');
      }

    } catch (error) {
      console.error('❌ Error downloading video:', error);
      toast.error('Failed to download video');
    } finally {
      // 다운로드 완료 - 상태 해제
      setDownloadingVideo(prev => ({ ...prev, [collaboration._id]: false }));
    }
  };

  const sendDeadlineReminder = async (collabId: string, handle: string) => {
    try {
      setSendingReminder(prev => ({ ...prev, [collabId]: true }));

      const response = await fetch(`${API_URL}/api/collaborations/${collabId}/send-deadline-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Deadline reminder sent to ${handle}`);
      } else {
        toast.error(data.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending deadline reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(prev => ({ ...prev, [collabId]: false }));
    }
  };

  const handleOpenReminderModal = () => {
    const now = new Date();
    const overdueCollabs = filteredCollaborations.filter(collab =>
      collab.deadline &&
      new Date(collab.deadline) < now &&
      ['awaiting_script', 'awaiting_video', 'awaiting_post'].includes(collab.collaborationStage)
    );

    const grouped = {
      script: overdueCollabs.filter(c => c.collaborationStage === 'awaiting_script'),
      video: overdueCollabs.filter(c => c.collaborationStage === 'awaiting_video'),
      post: overdueCollabs.filter(c => c.collaborationStage === 'awaiting_post')
    };

    setReminderTargets(grouped);
    setShowReminderModal(true);
  };

  const sendAllDeadlineReminders = async () => {
    const allTargets = [
      ...reminderTargets.script,
      ...reminderTargets.video,
      ...reminderTargets.post
    ];

    if (allTargets.length === 0) {
      toast.error('No targets to send reminders to');
      return;
    }

    const confirmed = window.confirm(`Send deadline reminders to ${allTargets.length} creators?`);
    if (!confirmed) return;

    setSendingAllReminders(true);
    setShowReminderModal(false);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < allTargets.length; i++) {
      const target = allTargets[i];
      try {
        const response = await fetch(`${API_URL}/api/collaborations/${target._id}/send-deadline-reminder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
        });

        const data = await response.json();

        if (data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`Error sending reminder to ${target.handle}:`, error);
        failCount++;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setSendingAllReminders(false);

    if (successCount > 0) {
      toast.success(`Successfully sent ${successCount} reminder${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to send ${failCount} reminder${failCount > 1 ? 's' : ''}`);
    }
  };

  const handlePauseToggle = async (collabId: string, handle: string, currentPaused: boolean) => {
    const action = currentPaused ? 'unpause' : 'pause';
    const confirmed = window.confirm(`Are you sure you want to ${action} the collaboration for @${handle}?`);
    
    if (!confirmed) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collabId}/admin/pause`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          paused: !currentPaused
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh the collaborations list
        await fetchCollaborations();
        alert(`Collaboration for @${handle} ${action}d successfully`);
      } else {
        alert(`Failed to ${action} collaboration: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error ${action}ing collaboration:`, error);
      alert(`Failed to ${action} collaboration`);
    }
  };

  const handleDelete = async (collabId: string, handle: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete the collaboration for @${handle}?\n\nThis action cannot be undone.`);

    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collabId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
      });

      const data = await response.json();

      if (data.success) {
        // Refresh the collaborations list
        await fetchCollaborations();
        alert(`Collaboration for @${handle} deleted successfully`);
      } else {
        alert(`Failed to delete collaboration: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting collaboration:', error);
      alert('Failed to delete collaboration');
    }
  };

  // Notes handlers
  const openNotesModal = (collab: Collaboration) => {
    setSelectedCollabForNotes(collab);
    setNotesContent(collab.notes || '');
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setSelectedCollabForNotes(null);
    setNotesContent('');
  };

  const handleSaveNotes = async () => {
    if (!selectedCollabForNotes) return;

    setSavingNotes(true);
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${selectedCollabForNotes._id}/notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ notes: notesContent }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Notes saved');
        setCollaborations(prevCollabs =>
          prevCollabs.map(collab =>
            collab._id === selectedCollabForNotes._id
              ? { ...collab, notes: notesContent }
              : collab
          )
        );
        closeNotesModal();
      } else {
        toast.error(data.error || 'Failed to save notes');
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUpdateVideoTopic = async (collabId: string, newTopic: string) => {
    try {
      const response = await fetch(`${API_URL}/api/collaborations/${collabId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ videoTopic: newTopic }),
      });

      const data = await response.json();

      if (data.success) {
        // Update the local state optimistically
        setCollaborations(prevCollabs =>
          prevCollabs.map(collab =>
            collab._id === collabId
              ? { ...collab, videoTopic: newTopic }
              : collab
          )
        );
        toast.success('Topic updated successfully');
      } else {
        toast.error(`Failed to update topic: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating video topic:', error);
      toast.error('Failed to update topic');
    } finally {
      setEditingTopicId(null);
      setEditingTopicValue('');
    }
  };

  const handleReject = async (collabId: string, type: 'script' | 'video') => {
    try {
      const formData = new FormData();
      formData.append('approved', 'false');
      formData.append('feedback', feedback || 'Please revise and resubmit.');
      
      // Add files if any
      feedbackFiles.forEach((file, index) => {
        formData.append(`feedbackFiles`, file);
      });

      const response = await fetch(`${API_URL}/api/collaborations/${collabId}/${type}/approve`, {
        method: 'PATCH',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData,
      });

      if (response.ok) {
        fetchCollaborations();
        setSelectedCollab(null);
        setFeedback('');
        setFeedbackFiles([]);
      }
    } catch (error) {
      console.error('Error rejecting:', error);
    }
  };

  const handleUpdateCompletedResponsesStatus = async () => {
    try {
      setIsUpdatingStatuses(true);
      setUpdateResult(null);
      
      const response = await fetch(`${API_URL}/api/collaborations/update-completed-responses-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
      });

      const data = await response.json();
      
      if (data.success) {
        setUpdateResult(data);
        alert(`성공적으로 ${data.updated}개의 response status를 'done'으로 변경했습니다.`);
      } else {
        alert(`오류가 발생했습니다: ${data.error || data.message}`);
      }
    } catch (error) {
      console.error('Error updating completed responses status:', error);
      alert('API 호출 중 오류가 발생했습니다.');
    } finally {
      setIsUpdatingStatuses(false);
    }
  };

  const getStageInfo = (stage: string, paused?: boolean): StageInfo => {
    // 일시중단된 경우 Paused로 표시
    if (paused) {
      return {
        label: 'Paused',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        icon: '⏸️',
        progress: 0
      };
    }

    const stages: { [key: string]: StageInfo } = {
      'paused': {
        label: 'Paused',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        icon: '⏸️',
        progress: 0
      },
      'scheduled': {
        label: 'Scheduled',
        color: '#6c757d',
        bgColor: 'rgba(108, 117, 125, 0.1)',
        icon: '📅',
        progress: 5
      },
      'awaiting_script': {
        label: 'Awaiting Script',
        color: '#ffc107',
        bgColor: 'rgba(255, 193, 7, 0.1)',
        icon: '📝',
        progress: 15
      },
      'reviewing_script': {
        label: 'Script Review',
        color: '#ff9800',
        bgColor: 'rgba(255, 152, 0, 0.1)',
        icon: '📄',
        progress: 30
      },
      'awaiting_video': {
        label: 'Awaiting Video',
        color: '#17a2b8',
        bgColor: 'rgba(23, 162, 184, 0.1)',
        icon: '🎥',
        progress: 45
      },
      'reviewing_video': {
        label: 'Video Review',
        color: '#fd7e14',
        bgColor: 'rgba(253, 126, 20, 0.1)',
        icon: '🎬',
        progress: 60
      },
      'awaiting_post': {
        label: 'Awaiting Post',
        color: '#007bff',
        bgColor: 'rgba(0, 123, 255, 0.1)',
        icon: '📱',
        progress: 75
      },
      'reviewing_post': {
        label: 'Post Review',
        color: '#dc3545',
        bgColor: 'rgba(220, 53, 69, 0.1)',
        icon: '📋',
        progress: 80
      },
      'awaiting_payment_info': {
        label: 'Awaiting Payment Info',
        color: '#6f42c1',
        bgColor: 'rgba(111, 66, 193, 0.1)',
        icon: '💳',
        progress: 90
      },
      'processing_payment': {
        label: 'Processing Payment',
        color: '#17a2b8',
        bgColor: 'rgba(23, 162, 184, 0.1)',
        icon: '💰',
        progress: 95
      },
      'completed': {
        label: 'Completed',
        color: '#28a745',
        bgColor: 'rgba(40, 167, 69, 0.1)',
        icon: '✅',
        progress: 100
      }
    };
    
    return stages[stage] || {
      label: stage,
      color: '#6c757d',
      bgColor: 'rgba(108, 117, 125, 0.1)',
      icon: '📋',
      progress: 0
    };
  };

  const getSocialMediaUrl = (platform: string, handle: string): string => {
    const cleanHandle = handle.replace('@', '');
    switch (platform.toLowerCase()) {
      case 'instagram':
        return `https://www.instagram.com/${cleanHandle}`;
      case 'tiktok':
        return `https://www.tiktok.com/@${cleanHandle}`;
      case 'youtube':
        return `https://www.youtube.com/@${cleanHandle}`;
      case 'twitter':
      case 'x':
        return `https://x.com/${cleanHandle}`;
      case 'facebook':
        return `https://www.facebook.com/${cleanHandle}`;
      case 'linkedin':
        return `https://www.linkedin.com/in/${cleanHandle}`;
      case 'twitch':
        return `https://www.twitch.tv/${cleanHandle}`;
      default:
        return '#';
    }
  };

  const filteredCollaborations = collaborations.filter(collab => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      // Remove @ symbol from handle for better search experience
      const handleWithoutAt = (collab.handle || '').replace('@', '').toLowerCase();
      return handleWithoutAt.includes(search) ||
             (collab.handle || '').toLowerCase().includes(search) ||
             (collab.email || '').toLowerCase().includes(search) ||
             (collab.platform || '').toLowerCase().includes(search);
    }
    return true;
  });

  // Multi-select functions
  const toggleSelectCollaboration = (collaborationId: string) => {
    setSelectedCollaborations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collaborationId)) {
        newSet.delete(collaborationId);
      } else {
        newSet.add(collaborationId);
      }
      return newSet;
    });
  };

  const toggleSelectAllCollaborations = () => {
    if (selectAllCollaborations) {
      setSelectedCollaborations(new Set());
      setSelectAllCollaborations(false);
    } else {
      const allIds = filteredCollaborations.map(collab => collab._id);
      setSelectedCollaborations(new Set(allIds));
      setSelectAllCollaborations(true);
    }
  };

  const deleteSelectedCollaborations = async () => {
    if (selectedCollaborations.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCollaborations.size} collaborations?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/collaborations/batch-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          collaborationIds: Array.from(selectedCollaborations)
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Successfully deleted ${result.deletedCount} collaborations.`);
        setSelectedCollaborations(new Set());
        setSelectAllCollaborations(false);
        fetchCollaborations();
      } else {
        alert(`Delete failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('An error occurred while deleting collaborations.');
    }
  };

  const fetchMetricsForSelected = async () => {
    if (selectedCollaborations.size === 0) return;
    
    if (!window.confirm(`Fetch post metrics for ${selectedCollaborations.size} collaborations?`)) return;
    
    let toastId: any = null;
    let eventSource: EventSource | null = null;
    
    try {
      // Create progress toast
      toastId = toast.loading("Starting metrics fetch...", {
        progress: 0,
        autoClose: false,
        closeOnClick: false,
        draggable: false
      });

      // Send POST request to start SSE stream
      const response = await fetch(`${API_URL}/api/collaborations/batch-fetch-metrics-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          collaborationIds: Array.from(selectedCollaborations)
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Create EventSource to receive real-time updates
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body reader not available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log('Stream completed');
              break;
            }

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep the incomplete line in buffer

            for (const line of lines) {
              if (line.trim() === '') continue;
              
              if (line.startsWith('data: ')) {
                const dataStr = line.slice(6); // Remove 'data: ' prefix
                
                try {
                  const data = JSON.parse(dataStr);
                  console.log('SSE received:', data);

                  switch (data.type) {
                    case 'start':
                      toast.update(toastId, {
                        render: `Starting metrics fetch for ${data.total} collaborations...`,
                        progress: 0
                      });
                      break;
                      
                    case 'progress':
                      const progress = data.current / data.total;
                      toast.update(toastId, {
                        render: `${data.message} (${data.current + 1}/${data.total})`,
                        progress: progress
                      });
                      break;
                      
                    case 'complete':
                      toast.update(toastId, {
                        render: `✅ ${data.message}`,
                        type: 'success',
                        progress: 1,
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                        isLoading: false
                      });
                      
                      // Update UI
                      setSelectedCollaborations(new Set());
                      setSelectAllCollaborations(false);
                      fetchCollaborations();
                      return; // Exit the loop
                      
                    case 'error':
                      toast.update(toastId, {
                        render: `❌ Error: ${data.error}`,
                        type: 'error',
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                        isLoading: false
                      });
                      return; // Exit the loop
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError, 'Raw data:', dataStr);
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Stream processing error:', streamError);
          if (toastId) {
            const errorMessage = streamError instanceof Error ? streamError.message : 'Unknown stream error';
            toast.update(toastId, {
              render: `❌ Stream error: ${errorMessage}`,
              type: 'error',
              autoClose: 5000,
              closeOnClick: true,
              draggable: true,
              isLoading: false
            });
          }
        }
      };

      // Start processing the stream
      await processStream();

    } catch (error) {
      console.error('Fetch metrics error:', error);
      if (toastId) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.update(toastId, {
          render: `❌ An error occurred: ${errorMessage}`,
          type: 'error',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          isLoading: false
        });
      } else {
        toast.error('An error occurred while fetching metrics.');
      }
    }
  };

  const fetchMetricsByTimeRange = async (months: number, isWeeks: boolean = false) => {
    try {
      setFetchingBulkMetrics(true);

      // Calculate date range
      const now = new Date();
      const startDate = new Date();

      if (isWeeks) {
        startDate.setDate(now.getDate() - (months * 7));
      } else {
        startDate.setMonth(now.getMonth() - months);
      }

      // Filter completed collaborations posted within the time range
      const targetCollaborations = collaborations.filter(collab =>
        collab.collaborationStage === 'completed' &&
        collab.postMetrics?.primaryPostedAt &&
        new Date(collab.postMetrics.primaryPostedAt) >= startDate
      );

      const timeLabel = isWeeks ? `${months} week${months > 1 ? 's' : ''}` : `${months} month${months > 1 ? 's' : ''}`;

      if (targetCollaborations.length === 0) {
        toast.info(`No completed collaborations posted in the last ${timeLabel}.`);
        setFetchingBulkMetrics(false);
        return;
      }

      // Confirmation dialog
      const confirmed = window.confirm(
        `Recalculate metrics for ${targetCollaborations.length} completed collaboration${targetCollaborations.length > 1 ? 's' : ''} posted in the last ${timeLabel}?`
      );

      if (!confirmed) {
        setFetchingBulkMetrics(false);
        return;
      }

      // Use the existing fetchMetricsForSelected logic
      let toastId: any = null;

      try {
        // Create progress toast
        toastId = toast.loading("Starting metrics fetch...", {
          progress: 0,
          autoClose: false,
          closeOnClick: false,
          draggable: false
        });

        // Send POST request to start SSE stream
        const response = await fetch(`${API_URL}/api/collaborations/batch-fetch-metrics-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            collaborationIds: targetCollaborations.map(c => c._id)
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Create EventSource to receive real-time updates
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body reader not available');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                console.log('Stream completed');
                break;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (!line.trim() || !line.startsWith('data: ')) continue;

                const dataStr = line.substring(6);

                try {
                  const data = JSON.parse(dataStr);

                  switch (data.type) {
                    case 'progress':
                      const percentage = data.percentage || Math.round((data.current / data.total) * 100);
                      toast.update(toastId, {
                        render: `📊 Fetching metrics: ${data.current}/${data.total} (${percentage}%)`,
                        progress: data.current / data.total
                      });
                      break;

                    case 'complete':
                      toast.update(toastId, {
                        render: `✅ Successfully fetched metrics for ${data.successful}/${data.total} collaborations${data.failed > 0 ? ` (${data.failed} failed)` : ''}`,
                        type: 'success',
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                        isLoading: false
                      });

                      // Update UI
                      fetchCollaborations();
                      return; // Exit the loop

                    case 'error':
                      toast.update(toastId, {
                        render: `❌ Error: ${data.error}`,
                        type: 'error',
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                        isLoading: false
                      });
                      return; // Exit the loop
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError, 'Raw data:', dataStr);
                }
              }
            }
          } catch (streamError) {
            console.error('Stream processing error:', streamError);
            if (toastId) {
              const errorMessage = streamError instanceof Error ? streamError.message : 'Unknown stream error';
              toast.update(toastId, {
                render: `❌ Stream error: ${errorMessage}`,
                type: 'error',
                autoClose: 5000,
                closeOnClick: true,
                draggable: true,
                isLoading: false
              });
            }
          }
        };

        // Start processing the stream
        await processStream();

      } catch (error) {
        console.error('Fetch metrics error:', error);
        if (toastId) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          toast.update(toastId, {
            render: `❌ Failed: ${errorMessage}`,
            type: 'error',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
            isLoading: false
          });
        } else {
          toast.error('An error occurred while fetching metrics.');
        }
      }

    } finally {
      setFetchingBulkMetrics(false);
      setShowMetricsDropdown(false);
    }
  };

  const getAveragePrice = () => {
    if (collaborations.length === 0) return 0;
    const total = collaborations.reduce((sum, collab) => sum + collab.agreedPrice, 0);
    return Math.round(total / collaborations.length);
  };

  return (
    <div className="collaborations-container full-width">
      {/* Header */}
      <div className="collaborations-header">
        <div className="header-content">
          <div>
            <h1 className="header-title">Collaborations</h1>
            <p className="header-subtitle">
              Manage and track all your creator partnerships in one unified dashboard
            </p>
          </div>
          <div className="header-actions">
            <button
              onClick={() => setShowEmailSettingsModal(true)}
              style={{
                backgroundColor: 'transparent',
                color: 'var(--text-primary, #374151)',
                border: '1px solid var(--border-color, #e5e7eb)',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
            >
              Email Settings
            </button>
            <button
              className="btn-update-status"
              onClick={handleUpdateCompletedResponsesStatus}
              disabled={isUpdatingStatuses}
              style={{
                backgroundColor: isUpdatingStatuses ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: isUpdatingStatuses ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isUpdatingStatuses ? '⏳' : '✅'} {isUpdatingStatuses ? 'Updating...' : 'Update Response Status'}
            </button>
            <button
              className="btn-new-collab"
              onClick={() => setShowCreateModal(true)}
            >
              ➕ New Collaboration
            </button>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="stats-dashboard">

        <div className="stat-card non-clickable">
          <div className="stat-header">
            <div className="stat-info">
              <h3>Average Price</h3>
              <div className="stat-value">${getAveragePrice()}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
              📈
            </div>
          </div>
        </div>

        <div
          className={`stat-card clickable ${filter === '' ? 'active' : ''}`}
          onClick={() => {
            console.log('Setting filter to: (all)');
            handleFilterChange('');
          }}
        >
          <div className="stat-header">
            <div className="stat-info">
              <h3>Total</h3>
              <div className="stat-value">{collaborations.length}</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
              📊
            </div>
          </div>
        </div>

        {stats
          .sort((a, b) => {
            // 상태별 정렬 순서 정의
            const stageOrder: { [key: string]: number } = {
              'scheduled': 1,
              'awaiting_script': 2,
              'reviewing_script': 3,
              'awaiting_video': 4,
              'reviewing_video': 5,
              'awaiting_post': 6,
              'reviewing_post': 7,
              'awaiting_payment_info': 8,
              'processing_payment': 9,
              'completed': 10
            };

            return (stageOrder[a._id] || 999) - (stageOrder[b._id] || 999);
          })
          .map(stat => {
            const stageInfo = getStageInfo(stat._id);
            return (
              <div 
                key={stat._id} 
                className={`stat-card clickable ${filter === stat._id ? 'active' : ''}`}
                onClick={() => {
                  console.log('Setting filter to:', stat._id);
                  handleFilterChange(stat._id);
                }}
              >
                <div className="stat-header">
                  <div className="stat-info">
                    <h3>{stageInfo.label}</h3>
                    <div className="stat-value">{stat.count}</div>
                  </div>
                  <div className="stat-icon" style={{ background: stageInfo.bgColor, color: stageInfo.color }}>
                    {stageInfo.icon}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Filter Section */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by creator, email, or platform..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="search-input"
            />
          </div>

          {(() => {
            const now = new Date();
            const overdueCount = filteredCollaborations.filter(collab =>
              collab.deadline &&
              new Date(collab.deadline) < now &&
              ['awaiting_script', 'awaiting_video', 'awaiting_post'].includes(collab.collaborationStage)
            ).length;

            return (
              <button
                onClick={handleOpenReminderModal}
                disabled={sendingAllReminders || overdueCount === 0}
                style={{
                  background: sendingAllReminders ? '#ccc' : (overdueCount > 0 ? '#f59e0b' : '#e9ecef'),
                  color: overdueCount > 0 ? 'white' : '#adb5bd',
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: sendingAllReminders || overdueCount === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginRight: '8px'
                }}
              >
                {sendingAllReminders ? '⏳ Sending...' : `📧 Remind All (${overdueCount})`}
              </button>
            );
          })()}

          <div
            className="metrics-dropdown-container"
            onMouseEnter={() => setShowMetricsDropdown(true)}
            onMouseLeave={() => setShowMetricsDropdown(false)}
          >
            <button
              className="metrics-dropdown-button"
              disabled={fetchingBulkMetrics}
              style={{
                background: fetchingBulkMetrics ? '#ccc' : '#10b981',
                color: 'white',
                padding: '10px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: fetchingBulkMetrics ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                marginRight: '8px'
              }}
            >
              {fetchingBulkMetrics ? '⏳ Fetching...' : '📊 Fetch Metrics ▼'}
            </button>
            {showMetricsDropdown && !fetchingBulkMetrics && (
              <div className="metrics-dropdown-menu">
                <button
                  className="metrics-dropdown-item"
                  onClick={() => fetchMetricsByTimeRange(1, true)}
                >
                  1 week
                </button>
                <button
                  className="metrics-dropdown-item"
                  onClick={() => fetchMetricsByTimeRange(2, true)}
                >
                  2 weeks
                </button>
                <button
                  className="metrics-dropdown-item"
                  onClick={() => fetchMetricsByTimeRange(1)}
                >
                  1 month
                </button>
                <button
                  className="metrics-dropdown-item"
                  onClick={() => fetchMetricsByTimeRange(3)}
                >
                  3 months
                </button>
                <button
                  className="metrics-dropdown-item"
                  onClick={() => fetchMetricsByTimeRange(6)}
                >
                  6 months
                </button>
                <button
                  className="metrics-dropdown-item"
                  onClick={() => fetchMetricsByTimeRange(12)}
                >
                  12 months
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowColumnCustomizer(!showColumnCustomizer)}
            className="btn-columns"
          >
            ⚙️ Columns
          </button>
          
        </div>
      </div>

      {/* Column Customizer */}
      {showColumnCustomizer && (
        <div className="column-customizer">
          <h4>Customize Columns</h4>
          <div className="column-list">
            {columns.map((col, index) => col.key !== 'actions' && (
              <div
                key={col.key}
                className={`column-item ${
                  draggedColumn === index ? 'dragging' : ''
                } ${
                  dragOverIndex === index ? 'drag-over' : ''
                }`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <span className="drag-handle">≡</span>
                <label className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={col.visible}
                    onChange={() => toggleColumnVisibility(col.key)}
                  />
                  <span>{col.label}</span>
                </label>
              </div>
            ))}
          </div>
          <button 
            onClick={() => setShowColumnCustomizer(false)}
            className="btn-close"
          >
            Close
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading collaborations...</div>
        </div>
      ) : filteredCollaborations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h2 className="empty-title">No collaborations found</h2>
          <p className="empty-description">
            {searchTerm ? 'Try adjusting your search criteria' : 'Start by creating a new collaboration'}
          </p>
          {!searchTerm && (
            <button className="btn-empty-action">
              Create Your First Collaboration
            </button>
          )}
        </div>
      ) : (
        (
            <div className="table-container">
              <table className="collaborations-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px', padding: '12px' }}>
                      <input
                        type="checkbox"
                        checked={selectAllCollaborations}
                        onChange={toggleSelectAllCollaborations}
                        className="rounded"
                      />
                    </th>
                    {columns.filter(col => col.visible).map(col => (
                      <th 
                        key={col.key}
                        onClick={() => col.sortable && handleSort(col.key)}
                        className={col.sortable ? 'sortable' : ''}
                      >
                        <div className="th-content">
                          <span>{col.label}</span>
                          {col.sortable && sortBy === col.key && (
                            <span className="sort-indicator">
                              {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCollaborations.map(collab => {
                    const stageInfo = getStageInfo(collab.collaborationStage, collab.paused);
                    return (
                      <tr key={collab._id} className={selectedCollaborations.has(collab._id) ? 'selected-row' : ''}>
                        <td style={{ width: '50px', padding: '12px' }}>
                          <input
                            type="checkbox"
                            checked={selectedCollaborations.has(collab._id)}
                            onChange={() => toggleSelectCollaboration(collab._id)}
                            className="rounded"
                          />
                        </td>
                        {columns.filter(col => col.visible).map(col => {
                          switch(col.key) {
                            case 'handle':
                              const cleanHandle = collab.handle.replace('@', '').toLowerCase();
                              const isInBatch = batchCreators.has(cleanHandle);

                              // Dynamically generate creator page link
                              const creatorPageUrl = collab.handle && collab.platform
                                ? `/creators/${collab.platform.toLowerCase()}/${collab.handle}`
                                : null;

                              return (
                                <td key={col.key}>
                                  <div className="creator-cell">
                                    {creatorPageUrl ? (
                                      <a
                                        href={`${window.location.origin}${creatorPageUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: isInBatch ? '#10b981' : 'var(--color-primary)',
                                          fontWeight: isInBatch ? '600' : '500',
                                          textDecoration: 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.textDecoration = 'underline';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.textDecoration = 'none';
                                        }}
                                      >
                                        <strong>{collab.handle}</strong>
                                      </a>
                                    ) : (
                                      <strong style={{
                                        color: isInBatch ? '#10b981' : 'inherit',
                                        fontWeight: isInBatch ? '600' : '500'
                                      }}>
                                        {collab.handle}
                                      </strong>
                                    )}
                                    <div className="text-secondary">{collab.email}</div>
                                  </div>
                                </td>
                              );
                            case 'email':
                              return <td key={col.key}>{collab.email}</td>;
                            case 'platform':
                              return (
                                <td key={col.key}>
                                  <a 
                                    href={getSocialMediaUrl(collab.platform, collab.handle)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`platform-badge platform-${collab.platform.toLowerCase()}`}
                                  >
                                    {collab.platform}
                                  </a>
                                </td>
                              );
                            case 'agreedPrice':
                              return (
                                <td key={col.key}>
                                  {(collab as any).pricingType === 'rpm' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span
                                        style={{
                                          background: '#dbeafe',
                                          color: '#1e40af',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          fontSize: '11px',
                                          fontWeight: '600'
                                        }}
                                        title={`RPM: $${(collab as any).rpmRate}/1000 views (max: $${(collab as any).maxPrice})`}
                                      >
                                        RPM ${(collab as any).rpmRate}
                                      </span>
                                      {(collab as any).calculatedPrice && (
                                        <span style={{ fontSize: '13px', color: '#059669' }}>
                                          ${(collab as any).calculatedPrice.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    `$${collab.agreedPrice}`
                                  )}
                                </td>
                              );
                            case 'videoTopic':
                              return (
                                <td
                                  key={col.key}
                                  title={editingTopicId === collab._id ? undefined : collab.videoTopic}
                                  style={{ cursor: editingTopicId === collab._id ? 'default' : 'pointer' }}
                                  onClick={() => {
                                    if (editingTopicId !== collab._id) {
                                      setEditingTopicId(collab._id);
                                      setEditingTopicValue(collab.videoTopic || '');
                                    }
                                  }}
                                >
                                  {editingTopicId === collab._id ? (
                                    <input
                                      type="text"
                                      value={editingTopicValue}
                                      onChange={(e) => setEditingTopicValue(e.target.value)}
                                      onBlur={() => {
                                        if (editingTopicValue !== collab.videoTopic) {
                                          handleUpdateVideoTopic(collab._id, editingTopicValue);
                                        } else {
                                          setEditingTopicId(null);
                                          setEditingTopicValue('');
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          if (editingTopicValue !== collab.videoTopic) {
                                            handleUpdateVideoTopic(collab._id, editingTopicValue);
                                          } else {
                                            setEditingTopicId(null);
                                            setEditingTopicValue('');
                                          }
                                        } else if (e.key === 'Escape') {
                                          setEditingTopicId(null);
                                          setEditingTopicValue('');
                                        }
                                      }}
                                      autoFocus
                                      style={{
                                        width: '100%',
                                        minWidth: '200px',
                                        maxWidth: '400px',
                                        padding: '4px 8px',
                                        border: '1px solid #3b82f6',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        outline: 'none'
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    collab.videoTopic ? (
                                      <span style={{
                                        display: 'inline-block',
                                        maxWidth: '400px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {collab.videoTopic}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#999' }}>-</span>
                                    )
                                  )}
                                </td>
                              );
                            case 'mentions':
                              return (
                                <td key={col.key}>
                                  {collab.mentions ? (
                                    <span style={{ 
                                      fontWeight: collab.mentions > 10 ? 'bold' : 'normal',
                                      color: collab.mentions > 20 ? '#10b981' : 
                                             collab.mentions > 10 ? '#3b82f6' : 
                                             'inherit'
                                    }}>
                                      {collab.mentions}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#9ca3af' }}>0</span>
                                  )}
                                </td>
                              );
                            case 'paymentStatus':
                              return (
                                <td key={col.key}>
                                  <span className={`payment-status status-${collab.paymentStatus || 'pending'}`}>
                                    {collab.paymentStatus || 'pending'}
                                  </span>
                                </td>
                              );
                            case 'paymentAmount':
                              return <td key={col.key}>{collab.paymentAmount ? `$${collab.paymentAmount}` : '-'}</td>;
                            case 'views':
                            case 'primaryViews':
                              return (
                                <td key={col.key}>
                                  {collab.postMetrics?.primaryViews ? (
                                    <>
                                      {collab.postMetrics.primaryViews.toLocaleString('en-US')}
                                      <span className="text-secondary ml-1">
                                        ({collab.postMetrics.primaryPlatform === 'tiktok' ? 'TT' : 'IG'})
                                      </span>
                                    </>
                                  ) : '-'}
                                </td>
                              );
                            case 'tiktokViews':
                              return (
                                <td key={col.key}>
                                  {collab.postMetrics?.tiktokViews 
                                    ? collab.postMetrics.tiktokViews.toLocaleString('en-US')
                                    : '-'}
                                </td>
                              );
                            case 'instagramViews':
                              return (
                                <td key={col.key}>
                                  {collab.postMetrics?.instagramViews 
                                    ? collab.postMetrics.instagramViews.toLocaleString('en-US')
                                    : '-'}
                                </td>
                              );
                            case 'postUrlTikTok':
                              return (
                                <td key={col.key}>
                                  {collab.postUrlTikTok ? (
                                    <a 
                                      href={collab.postUrlTikTok} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="post-link"
                                      title="View TikTok post"
                                    >
                                      🔗 View
                                    </a>
                                  ) : '-'}
                                </td>
                              );
                            case 'postUrlInstagram':
                              return (
                                <td key={col.key}>
                                  {collab.postUrlInstagram ? (
                                    <a 
                                      href={collab.postUrlInstagram} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="post-link"
                                      title="View Instagram post"
                                    >
                                      🔗 View
                                    </a>
                                  ) : '-'}
                                </td>
                              );
                            case 'postUrlYoutube':
                              return (
                                <td key={col.key}>
                                  {collab.postUrlYoutube ? (
                                    <a 
                                      href={collab.postUrlYoutube} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="post-link"
                                      title="View YouTube video"
                                    >
                                      🔗 View
                                    </a>
                                  ) : '-'}
                                </td>
                              );
                            case 'primaryPostedAt':
                              return (
                                <td key={col.key}>
                                  {collab.postMetrics?.primaryPostedAt
                                    ? new Date(collab.postMetrics.primaryPostedAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })
                                    : '-'}
                                </td>
                              );
                            case 'coupon':
                              return (
                                <td key={col.key} className="coupon-code">
                                  {collab.stripeCoupon?.couponCode || '-'}
                                </td>
                              );
                            case 'redeemed':
                              return (
                                <td key={col.key} className="coupon-redeemed">
                                  {collab.stripeCoupon?.redeemed ?? '-'}
                                </td>
                              );
                            case 'primaryPlatform':
                              return (
                                <td key={col.key}>
                                  {collab.postMetrics?.primaryPlatform ? (
                                    <span className={`platform-badge platform-${collab.postMetrics.primaryPlatform}`}>
                                      {collab.postMetrics.primaryPlatform === 'tiktok' ? 'TikTok' : 'Instagram'}
                                    </span>
                                  ) : '-'}
                                </td>
                              );
                            case 'cpm':
                              // Calculate CPM based on totalViews instead of primaryViews
                              const calculatedCpm = collab.totalViews && collab.totalViews > 0
                                ? (collab.agreedPrice / (collab.totalViews / 1000))
                                : collab.postMetrics?.cpm || collab.cpm;
                              return (
                                <td key={col.key}>
                                  {calculatedCpm ? `$${calculatedCpm.toFixed(2)}` : '-'}
                                </td>
                              );
                            case 'maxCPM':
                              return (
                                <td key={col.key}>
                                  {collab.maxCPM ? `$${collab.maxCPM.toFixed(2)}` : '-'}
                                </td>
                              );
                            case 'cpmDelta':
                              return (
                                <td key={col.key} style={{
                                  color: collab.cpmDelta 
                                    ? collab.cpmDelta > 0 ? '#10b981' : '#ef4444'
                                    : 'inherit',
                                  fontWeight: collab.cpmDelta ? '600' : 'normal'
                                }}>
                                  {collab.cpmDelta ? (
                                    <>
                                      {collab.cpmDelta > 0 ? '+' : ''}
                                      ${collab.cpmDelta.toFixed(2)}
                                    </>
                                  ) : '-'}
                                </td>
                              );
                            case 'sumChange':
                              return (
                                <td key={col.key} style={{
                                  color: collab.sumChange !== undefined && collab.sumChange !== null
                                    ? collab.sumChange > 20 ? '#10b981' : 
                                      collab.sumChange > 0 ? '#3b82f6' :
                                      collab.sumChange < 0 ? '#ef4444' : 'inherit'
                                    : 'inherit',
                                  fontWeight: collab.sumChange && Math.abs(collab.sumChange) > 20 ? '600' : 'normal',
                                  position: 'relative',
                                  cursor: 'help'
                                }}>
                                  <div className="sumchange-cell">
                                    {collab.sumChange !== undefined && collab.sumChange !== null ? (
                                      <>
                                        {collab.sumChange > 0 ? '+' : ''}
                                        {collab.sumChange.toFixed(2)}%
                                      </>
                                    ) : '-'}
                                    {(collab as any).paymentSum48hPast !== undefined && (collab as any).paymentSum48h !== undefined && (
                                      <div className="sumchange-tooltip">
                                        <div className="tooltip-row">
                                          <span className="tooltip-label">이전 48시간 매출:</span>
                                          <span className="tooltip-value">
                                            ${((collab as any).paymentSum48hPast || 0).toFixed(2)} 
                                            ({(collab as any).paymentCount48hPast || 0}건)
                                          </span>
                                        </div>
                                        <div className="tooltip-row">
                                          <span className="tooltip-label">이후 48시간 매출:</span>
                                          <span className="tooltip-value">
                                            ${((collab as any).paymentSum48h || 0).toFixed(2)} 
                                            ({(collab as any).paymentCount48h || 0}건)
                                          </span>
                                        </div>
                                        <div className="tooltip-divider"></div>
                                        <div className="tooltip-row">
                                          <span className="tooltip-label">변화율:</span>
                                          <span className="tooltip-value" style={{
                                            color: (collab.sumChange || 0) > 0 ? '#10b981' : '#ef4444',
                                            fontWeight: 'bold'
                                          }}>
                                            {(collab.sumChange || 0) > 0 ? '+' : ''}{(collab.sumChange || 0).toFixed(2)}%
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              );
                            case 'audienceData':
                              return (
                                <td key={col.key}>
                                  <div
                                    className="audience-cell audience-cell-clickable"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedCollabForAudience(collab);
                                      setShowAudienceModal(true);
                                    }}
                                    title="Click to edit audience distribution"
                                  >
                                    {collab.audienceData ? (
                                      <>
                                        {(() => {
                                          // Parse audience data if it's a string
                                          let audienceObj = collab.audienceData;
                                          if (typeof audienceObj === 'string') {
                                            try {
                                              audienceObj = JSON.parse(audienceObj);
                                            } catch {
                                              return <span>-</span>;
                                            }
                                          }

                                          // Convert to array and sort by percentage
                                          const countries = Object.entries(audienceObj)
                                            .map(([code, value]) => {
                                              let percentage = typeof value === 'object' && value !== null
                                                ? (value as any).percentage || 0
                                                : parseFloat(String(value)) || 0;

                                              // Convert 0-1 range to percentage if needed
                                              if (percentage <= 1) {
                                                percentage = percentage * 100;
                                              }

                                              return { code, percentage };
                                            })
                                            .sort((a, b) => b.percentage - a.percentage)
                                            .slice(0, 3);

                                          return (
                                            <div className="audience-flags">
                                              {countries.map((country, idx) => (
                                                <span key={idx} className="country-item">
                                                  {getCountryFlag(country.code)}
                                                  <span className="country-percentage">
                                                    {country.percentage.toFixed(0)}%
                                                  </span>
                                                </span>
                                              ))}
                                            </div>
                                          );
                                        })()}
                                      </>
                                    ) : <span className="add-audience">+ Add</span>}
                                  </div>
                                </td>
                              );
                            case 'totalViews':
                              return (
                                <td key={col.key}>
                                  {collab.totalViews ? collab.totalViews.toLocaleString('en-US') : '-'}
                                </td>
                              );
                            case 'postEngagementRate':
                              const postEngagementRate = collab.postMetrics?.engagementRate;
                              return (
                                <td key={col.key}>
                                  {postEngagementRate !== undefined && postEngagementRate !== null ? (
                                    <span style={{
                                      color: postEngagementRate > 5 ? '#10b981' : 
                                             postEngagementRate > 2 ? '#3b82f6' : 
                                             postEngagementRate > 0 ? '#f59e0b' : '#6b7280',
                                      fontWeight: postEngagementRate > 5 ? '600' : 'normal'
                                    }}>
                                      {postEngagementRate.toFixed(2)}%
                                    </span>
                                  ) : (
                                    <span style={{ color: '#9ca3af' }}>-</span>
                                  )}
                                </td>
                              );
                            case 'recentViews':
                              return <td key={col.key}>{collab.recentViews || '-'}</td>;
                            case 'collaborationStage':
                              return (
                                <td key={col.key}>
                                  <div 
                                    className="status-badge" 
                                    style={{ 
                                      background: stageInfo.bgColor,
                                      color: stageInfo.color,
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.875rem',
                                      display: 'inline-block'
                                    }}
                                  >
                                    {stageInfo.icon} {stageInfo.label}
                                  </div>
                                </td>
                              );
                            case 'deadline':
                              return (
                                <td key={col.key}>
                                  {collab.deadline ? (
                                    <span className={new Date(collab.deadline) < new Date() ? 'text-danger' : ''}>
                                      {new Date(collab.deadline).toLocaleDateString()}
                                    </span>
                                  ) : '-'}
                                </td>
                              );
                            case 'scriptApproved':
                              return (
                                <td key={col.key}>
                                  {collab.scriptApproved ? '✅' : collab.scriptSubmittedAt ? '⏳' : '-'}
                                </td>
                              );
                            case 'videoApproved':
                              return (
                                <td key={col.key}>
                                  {collab.videoApproved ? '✅' : collab.videoSubmittedAt ? '⏳' : '-'}
                                </td>
                              );
                            case 'postVerified':
                              return (
                                <td key={col.key}>
                                  {collab.postVerified ? '✅' : collab.postSubmittedAt ? '⏳' : '-'}
                                </td>
                              );
                            case 'primaryCountry':
                              return <td key={col.key}>{collab.primaryCountry || '-'}</td>;
                            case 'language':
                              return <td key={col.key}>{collab.language || '-'}</td>;
                            case 'affiliateStatus':
                              return <td key={col.key}>{collab.affiliateStatus || '-'}</td>;
                            case 'isLongTerm':
                              return <td key={col.key}>{collab.isLongTerm ? '✅' : '-'}</td>;
                            case 'emailSent':
                              return <td key={col.key}>{collab.emailSent ? '✅' : '-'}</td>;
                            case 'notes':
                              const noteText = collab.notes || '';
                              return (
                                <td key={col.key}>
                                  <div
                                    className="notes-cell-clickable"
                                    onClick={() => openNotesModal(collab)}
                                    title={noteText || 'Click to add notes'}
                                  >
                                    {noteText ? (
                                      <span className="notes-text">
                                        {noteText.substring(0, 40)}{noteText.length > 40 ? '...' : ''}
                                      </span>
                                    ) : (
                                      <span className="notes-add-btn">+ Note</span>
                                    )}
                                  </div>
                                </td>
                              );
                            case 'createdAt':
                              return <td key={col.key}>{new Date(collab.createdAt).toLocaleDateString()}</td>;
                            case 'actions':
                              const pausableStages = [
                                'awaiting_script', 
                                'reviewing_script', 
                                'awaiting_video', 
                                'reviewing_video', 
                                'awaiting_post', 
                                'reviewing_post'
                              ];
                              const canPause = pausableStages.includes(collab.collaborationStage);
                              
                              return (
                                <td key={col.key}>
                                  <div className="actions-cell">
                                    <button 
                                      className="btn-action btn-view"
                                      onClick={() => navigate(`/collaborations/${collab._id}`)}
                                      title="Admin View"
                                      style={{ fontSize: '11px', padding: '6px 4px', minWidth: '35px' }}
                                    >
                                      Admin
                                    </button>
                                    <button 
                                      className="btn-action btn-creator"
                                      onClick={() => window.open(`/collaborations-creator/${collab._id}`, '_blank')}
                                      title="Creator View"
                                      style={{ fontSize: '11px', padding: '6px 4px', minWidth: '40px' }}
                                    >
                                      Creator
                                    </button>
                                    {canPause && (
                                      <button 
                                        className="btn-action btn-pause"
                                        onClick={() => handlePauseToggle(collab._id, collab.handle, collab.paused || false)}
                                        title={collab.paused ? "Resume Collaboration" : "Pause Collaboration"}
                                        style={{
                                          background: collab.paused ? '#10b981' : '#ffc107',
                                          color: collab.paused ? 'white' : 'black',
                                          fontSize: '11px',
                                          padding: '6px 4px',
                                          minWidth: '35px'
                                        }}
                                      >
                                        {collab.paused ? 'Resume' : 'Pause'}
                                      </button>
                                    )}
                                    {collab.collaborationStage === 'completed' && (() => {
                                      const cleanHandle = collab.handle.replace('@', '').toLowerCase();
                                      const isInBatch = batchCreators.has(cleanHandle);
                                      return (
                                        <button
                                          className="btn-action btn-batch"
                                          onClick={() => !isInBatch && openBatchModal(collab)}
                                          title={isInBatch ? "Already in batch" : "Create Batch"}
                                          disabled={isInBatch}
                                          style={{
                                            fontSize: '11px',
                                            padding: '6px 4px',
                                            minWidth: '32px',
                                            ...(isInBatch && {
                                              backgroundColor: '#d1d5db',
                                              color: '#6b7280',
                                              cursor: 'not-allowed',
                                              opacity: 0.6
                                            })
                                          }}
                                        >
                                          {isInBatch ? 'In Batch' : 'Batch'}
                                        </button>
                                      );
                                    })()}
                                    {collab.collaborationStage === 'completed' && (
                                      <button 
                                        className="btn-action btn-fetch"
                                        onClick={() => fetchPostMetrics(collab._id)}
                                        disabled={fetchingMetrics[collab._id]}
                                        title="Fetch Post Metrics"
                                        style={{
                                          background: fetchingMetrics[collab._id] ? '#ccc' : '#10b981',
                                          color: 'white',
                                          fontSize: '11px',
                                          padding: '6px 4px',
                                          minWidth: '40px'
                                        }}
                                      >
                                        {fetchingMetrics[collab._id] ? 'Load' : 'Metrics'}
                                      </button>
                                    )}
                                    {collab.collaborationStage === 'completed' && (
                                      <>
                                        <button
                                          className="btn-action btn-download"
                                          onClick={() => downloadVideo(collab)}
                                          disabled={downloadingVideo[collab._id]}
                                          title={downloadingVideo[collab._id] ? "Downloading..." : "Download Video"}
                                          style={{
                                            background: downloadingVideo[collab._id] ? '#ccc' : '#3b82f6',
                                            color: 'white',
                                            fontSize: '11px',
                                            padding: '6px 4px',
                                            minWidth: '40px',
                                            marginLeft: '4px',
                                            cursor: downloadingVideo[collab._id] ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          {downloadingVideo[collab._id] ? '⬇️...' : 'Download'}
                                        </button>
                                      </>
                                    )}
                                    {collab.deadline && new Date(collab.deadline) < new Date() &&
                                     ['awaiting_script', 'awaiting_video', 'awaiting_post'].includes(collab.collaborationStage) && (
                                      <button
                                        className="btn-action btn-remind"
                                        onClick={() => sendDeadlineReminder(collab._id, collab.handle)}
                                        disabled={sendingReminder[collab._id]}
                                        title="Send Deadline Reminder"
                                        style={{
                                          background: sendingReminder[collab._id] ? '#ccc' : '#f59e0b',
                                          color: 'white',
                                          fontSize: '11px',
                                          padding: '6px 4px',
                                          minWidth: '40px',
                                          marginLeft: '4px',
                                          cursor: sendingReminder[collab._id] ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        {sendingReminder[collab._id] ? 'Sending...' : 'Remind'}
                                      </button>
                                    )}
                                    <button
                                      className="btn-action btn-delete"
                                      onClick={() => handleDelete(collab._id, collab.handle)}
                                      title="Delete Collaboration"
                                      style={{
                                        background: '#ef4444',
                                        color: 'white',
                                        marginLeft: '4px',
                                        fontSize: '11px',
                                        padding: '6px 4px',
                                        minWidth: '35px'
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              );
                            default:
                              return <td key={col.key}>-</td>;
                          }
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        )
      )}

      {/* Review Modal */}
      {selectedCollab && (
        <div className="review-modal-overlay" onClick={() => setSelectedCollab(null)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                Review {selectedCollab.collaborationStage === 'reviewing_script' ? 'Script' : 'Video'}
              </h2>
              <p className="modal-subtitle">
                From {selectedCollab.handle} • ${selectedCollab.agreedPrice}
              </p>
            </div>

            <div className="modal-body">
              {selectedCollab.collaborationStage === 'reviewing_script' && selectedCollab.scriptDraft && (
                <div className="content-section">
                  <h3 className="section-title">Script Content</h3>
                  <div className="content-box">
                    {selectedCollab.scriptDraft}
                  </div>
                </div>
              )}

              {selectedCollab.collaborationStage === 'reviewing_video' && selectedCollab.videoFile && (
                <div className="content-section">
                  <h3 className="section-title">Video Content</h3>
                  <video 
                    className="video-player" 
                    controls
                    preload="metadata"
                    style={{ 
                      display: 'block',
                      outline: 'none'
                    }}
                  >
                    <source src={selectedCollab.videoFile} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              <div className="feedback-section">
                <h3 className="section-title">Feedback</h3>
                <textarea
                  className="feedback-textarea"
                  placeholder="Provide feedback for the creator..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />
                
                {/* File Upload Section */}
                <div className="feedback-attachments">
                  <label className="file-upload-label">
                    📎 Attach files (images/videos)
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setFeedbackFiles(prev => [...prev, ...files]);
                      }}
                      className="file-upload-input"
                    />
                  </label>
                  
                  {/* Preview uploaded files */}
                  {feedbackFiles.length > 0 && (
                    <div className="uploaded-files">
                      {feedbackFiles.map((file, index) => (
                        <div key={index} className="uploaded-file">
                          <span className="file-name">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setFeedbackFiles(prev => prev.filter((_, i) => i !== index))}
                            className="file-remove-btn"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-modal btn-cancel" onClick={() => setSelectedCollab(null)}>
                Cancel
              </button>
              <button 
                className="btn-modal btn-reject"
                onClick={() => handleReject(
                  selectedCollab._id, 
                  selectedCollab.collaborationStage === 'reviewing_script' ? 'script' : 'video'
                )}
              >
                Request Changes
              </button>
              <button 
                className="btn-modal btn-approve"
                onClick={() => handleApprove(
                  selectedCollab._id, 
                  selectedCollab.collaborationStage === 'reviewing_script' ? 'script' : 'video'
                )}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedCollabForNotes && (
        <div className="notes-modal-overlay" onClick={closeNotesModal}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <h3>Notes</h3>
              <span className="notes-modal-handle">@{selectedCollabForNotes.handle}</span>
              <button className="notes-modal-close" onClick={closeNotesModal}>&times;</button>
            </div>

            <div className="notes-modal-body">
              <textarea
                value={notesContent}
                onChange={(e) => setNotesContent(e.target.value)}
                placeholder="Enter notes..."
                rows={6}
                className="notes-textarea"
              />
            </div>

            <div className="notes-modal-footer">
              <button
                className="notes-btn-cancel"
                onClick={closeNotesModal}
              >
                Cancel
              </button>
              <button
                className="notes-btn-save"
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Creation Modal */}
      {showBatchModal && selectedCollabForBatch && (
        <div className="batch-modal-overlay" onClick={() => setShowBatchModal(false)}>
          <div className="batch-modal" onClick={(e) => e.stopPropagation()}>
            <div className="batch-modal-header">
              <h2>Create Batch Proposal</h2>
              <p className="batch-modal-subtitle">
                For {selectedCollabForBatch.handle} • Original price: ${selectedCollabForBatch.agreedPrice}
                {selectedCollabForBatch.maxCPM && (
                  <span style={{ marginLeft: '8px' }}>
                    • Max CPM: ${selectedCollabForBatch.maxCPM.toFixed(2)}
                  </span>
                )}
                {selectedCollabForBatch.cpm && (
                  <span style={{ marginLeft: '8px' }}>
                    • Recent CPM: ${selectedCollabForBatch.cpm.toFixed(2)}
                  </span>
                )}
              </p>
            </div>

            <div className="batch-modal-body">
              {/* Pricing Type Selection */}
              <div className="pricing-type-section" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                  Pricing Type
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className={`pricing-type-btn ${batchPricingType === 'fixed' ? 'active' : ''}`}
                    onClick={() => {
                      setBatchPricingType('fixed');
                      setBatchPrice(Math.round(selectedCollabForBatch.agreedPrice * 0.8));
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: batchPricingType === 'fixed' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: batchPricingType === 'fixed' ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    💵 Fixed Price per Video
                  </button>
                  <button
                    type="button"
                    className={`pricing-type-btn ${batchPricingType === 'rpm' ? 'active' : ''}`}
                    onClick={() => {
                      setBatchPricingType('rpm');
                      // Set RPM Rate to Max CPM / 2, rounded to nearest integer
                      const defaultRpmRate = selectedCollabForBatch.maxCPM
                        ? Math.round(selectedCollabForBatch.maxCPM / 2)
                        : 10;
                      setBatchRpmRate(defaultRpmRate);
                      setBatchMaxPrice(Math.ceil((selectedCollabForBatch.agreedPrice * 2) / 500) * 500);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: batchPricingType === 'rpm' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: batchPricingType === 'rpm' ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    📊 RPM (Per 1000 Views)
                  </button>
                </div>
              </div>

              {/* Discount Selection (Fixed type only) */}
              {batchPricingType === 'fixed' && (
                <div className="discount-section">
                  <label>Quick Price Selection</label>
                <div className="discount-buttons">
                  <button
                    className="discount-btn"
                    onClick={() => setBatchPrice(selectedCollabForBatch.agreedPrice)}
                    type="button"
                  >
                    Original
                    <span className="discount-price">
                      ${selectedCollabForBatch.agreedPrice}
                    </span>
                  </button>
                  {[20, 30, 40].map(discount => (
                    <button
                      key={discount}
                      className="discount-btn"
                      onClick={() => handleDiscountSelect(discount)}
                      type="button"
                    >
                      {discount}% OFF
                      <span className="discount-price">
                        ${Math.round(selectedCollabForBatch.agreedPrice * (1 - discount / 100))}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Batch Configuration */}
              {!useExistingBatch && (
                <div className="batch-config-grid">
                  <div className="config-item">
                    <label>Batch Size</label>
                    <div className="input-group">
                      <input
                        type="number"
                        value={batchSize}
                        onChange={(e) => setBatchSize(parseInt(e.target.value) || 1)}
                        min="1"
                        max="20"
                      />
                      <span className="input-suffix">videos</span>
                    </div>
                  </div>

                  {batchPricingType === 'fixed' ? (
                    <div className="config-item">
                      <label>Price per Video</label>
                      <div className="input-group">
                        <span className="input-prefix">$</span>
                        <input
                          type="number"
                          value={batchPrice}
                          onChange={(e) => setBatchPrice(parseInt(e.target.value) || 0)}
                          min="0"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="config-item">
                        <label>RPM Rate</label>
                        <div className="input-group">
                          <span className="input-prefix">$</span>
                          <input
                            type="number"
                            value={batchRpmRate}
                            onChange={(e) => setBatchRpmRate(parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.5"
                          />
                          <span className="input-suffix"></span>
                        </div>
                      </div>

                      <div className="config-item">
                        <label>Max Price per Video</label>
                        <div className="input-group">
                          <span className="input-prefix">$</span>
                          <input
                            type="number"
                            value={batchMaxPrice}
                            onChange={(e) => setBatchMaxPrice(parseInt(e.target.value) || 0)}
                            min="0"
                            step="100"
                          />
                        </div>
                      </div>
                    </>
                  )}

                </div>
              )}

              {/* Message Section */}
              {!useExistingBatch && (
                <div className="message-section" style={{ marginTop: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Message for Creator (Optional)
                  </label>
                  <textarea
                    value={batchMessage}
                    onChange={(e) => setBatchMessage(e.target.value)}
                    placeholder="e.g., AI Clipping videos, Product review focus, etc."
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                    maxLength={500}
                  />
                  <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                    {batchMessage.length}/500
                  </div>
                </div>
              )}

              {/* Advanced Section */}
              <div className="advanced-section">
                <button 
                  className="advanced-toggle"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span className={`toggle-icon ${showAdvanced ? 'expanded' : ''}`}>▶</span>
                  Advanced Options
                </button>
                
                {showAdvanced && (
                  <div className="advanced-content">
                    <div className="existing-batch-option">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={useExistingBatch}
                          onChange={(e) => {
                            setUseExistingBatch(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedBatchId('');
                            }
                          }}
                        />
                        <span>Assign to Existing Batch</span>
                      </label>
                      
                      {useExistingBatch && (
                        <div className="batch-select-wrapper">
                          <select
                            value={selectedBatchId}
                            onChange={(e) => setSelectedBatchId(e.target.value)}
                            className="batch-select"
                          >
                            <option value="">Select a batch...</option>
                            {existingBatches.map(batch => (
                              <option key={batch._id} value={batch._id}>
                                {batch.handle} - Batch #{batch.batchCount} 
                                (${batch.batchPrice} × {batch.batchSize} videos)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              {!useExistingBatch && (
                <div className="batch-summary">
                  {batchPricingType === 'fixed' ? (
                    <>
                      <div className="summary-item">
                        <span>Discount Applied:</span>
                        <span className="summary-value">
                          {Math.round((1 - batchPrice / selectedCollabForBatch.agreedPrice) * 100)}%
                        </span>
                      </div>
                      <div className="summary-item total">
                        <span>Total Package Value:</span>
                        <span className="summary-value">${batchSize * batchPrice}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="summary-item">
                        <span>Pricing:</span>
                        <span className="summary-value">
                          RPM ${batchRpmRate} per 1000 views
                        </span>
                      </div>
                      <div className="summary-item">
                        <span>Max Price per Video:</span>
                        <span className="summary-value">${batchMaxPrice}</span>
                      </div>
                      <div className="summary-item total">
                        <span>Max Total Value:</span>
                        <span className="summary-value">${batchSize * batchMaxPrice}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="batch-modal-footer">
              <button className="btn-modal btn-cancel" onClick={() => setShowBatchModal(false)}>
                Cancel
              </button>
              <button
                className="btn-modal btn-create"
                onClick={createBatch}
                disabled={
                  useExistingBatch
                    ? !selectedBatchId
                    : (batchPricingType === 'fixed'
                        ? (!batchPrice || !batchSize)
                        : (!batchRpmRate || !batchMaxPrice || !batchSize))
                }
              >
                {useExistingBatch ? 'Assign to Batch' : 'Create Batch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deadline Reminder Modal */}
      {showReminderModal && (
        <div className="batch-modal-overlay" onClick={() => setShowReminderModal(false)}>
          <div className="batch-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="batch-modal-header">
              <h2>📧 Send Deadline Reminders</h2>
              <p className="batch-modal-subtitle">
                Review targets before sending
              </p>
            </div>

            <div className="batch-modal-body">
              {/* Summary */}
              <div style={{
                background: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                  Total: {reminderTargets.script.length + reminderTargets.video.length + reminderTargets.post.length} creators
                </div>
              </div>

              {/* Script Targets */}
              {reminderTargets.script.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#495057'
                  }}>
                    📝 Script Submission ({reminderTargets.script.length})
                  </h4>
                  <div style={{
                    background: '#fff',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    fontSize: '13px',
                    lineHeight: '1.6'
                  }}>
                    {reminderTargets.script.map(c => c.handle).join(', ')}
                  </div>
                </div>
              )}

              {/* Video Targets */}
              {reminderTargets.video.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#495057'
                  }}>
                    🎥 Video Submission ({reminderTargets.video.length})
                  </h4>
                  <div style={{
                    background: '#fff',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    fontSize: '13px',
                    lineHeight: '1.6'
                  }}>
                    {reminderTargets.video.map(c => c.handle).join(', ')}
                  </div>
                </div>
              )}

              {/* Post Targets */}
              {reminderTargets.post.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#495057'
                  }}>
                    📱 Post Upload ({reminderTargets.post.length})
                  </h4>
                  <div style={{
                    background: '#fff',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                    fontSize: '13px',
                    lineHeight: '1.6'
                  }}>
                    {reminderTargets.post.map(c => c.handle).join(', ')}
                  </div>
                </div>
              )}

              {/* No targets */}
              {reminderTargets.script.length === 0 &&
               reminderTargets.video.length === 0 &&
               reminderTargets.post.length === 0 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  No overdue collaborations found
                </div>
              )}
            </div>

            <div className="batch-modal-footer" style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              marginTop: '20px'
            }}>
              <button
                onClick={() => setShowReminderModal(false)}
                className="btn-cancel"
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendAllDeadlineReminders}
                disabled={reminderTargets.script.length === 0 &&
                         reminderTargets.video.length === 0 &&
                         reminderTargets.post.length === 0}
                className="btn-create"
                style={{
                  padding: '10px 20px',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: reminderTargets.script.length === 0 &&
                         reminderTargets.video.length === 0 &&
                         reminderTargets.post.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: reminderTargets.script.length === 0 &&
                          reminderTargets.video.length === 0 &&
                          reminderTargets.post.length === 0 ? 0.5 : 1
                }}
              >
                Send All Reminders
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Collaboration Modal */}
      {showCreateModal && (
        <div className="batch-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="batch-modal" onClick={(e) => e.stopPropagation()}>
            <div className="batch-modal-header">
              <h2>Create New Collaboration</h2>
              <p className="batch-modal-subtitle">
                Add a completed collaboration to track metrics and performance
              </p>
            </div>

            <div className="batch-modal-body">
              <div className="batch-config-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="config-item">
                  <label>Creator Handle *</label>
                  <div className="input-group">
                    <input
                      type="text"
                      value={createFormData.handle}
                      onChange={(e) => setCreateFormData({...createFormData, handle: e.target.value})}
                      placeholder="creator_name"
                      required
                    />
                  </div>
                </div>

                <div className="config-item">
                  <label>Instagram Post Link</label>
                  <div className="input-group">
                    <input
                      type="url"
                      value={createFormData.postUrlInstagram}
                      onChange={(e) => setCreateFormData({...createFormData, postUrlInstagram: e.target.value})}
                      placeholder="https://www.instagram.com/p/..."
                    />
                  </div>
                </div>

                <div className="config-item">
                  <label>TikTok Post Link</label>
                  <div className="input-group">
                    <input
                      type="url"
                      value={createFormData.postUrlTikTok}
                      onChange={(e) => setCreateFormData({...createFormData, postUrlTikTok: e.target.value})}
                      placeholder="https://www.tiktok.com/@username/video/..."
                    />
                  </div>
                </div>

                <div className="config-item">
                  <label>Agreed Price *</label>
                  <div className="input-group">
                    <span className="input-prefix">$</span>
                    <input
                      type="number"
                      value={createFormData.agreedPrice}
                      onChange={(e) => setCreateFormData({...createFormData, agreedPrice: e.target.value})}
                      placeholder="0"
                      min="0"
                      step="1"
                      required
                    />
                  </div>
                </div>

                <div className="config-item">
                  <label>Video Topic</label>
                  <div className="input-group">
                    <input
                      type="text"
                      value={createFormData.videoTopic}
                      onChange={(e) => setCreateFormData({...createFormData, videoTopic: e.target.value})}
                      placeholder="e.g., photo to tv ads"
                    />
                  </div>
                </div>

                <div className="config-item">
                  <label>PayPal Invoice Link</label>
                  <div className="input-group">
                    <input
                      type="url"
                      value={createFormData.invoiceLink}
                      onChange={(e) => setCreateFormData({...createFormData, invoiceLink: e.target.value})}
                      placeholder="https://www.paypal.com/invoice/..."
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="batch-summary" style={{ marginTop: '20px' }}>
                <div className="summary-item">
                  <span>Status:</span>
                  <span className="summary-value">Will be created as 'Completed'</span>
                </div>
                <div className="summary-item">
                  <span>Source Type:</span>
                  <span className="summary-value">Manual Entry</span>
                </div>
              </div>
            </div>

            <div className="batch-modal-footer">
              <button 
                className="btn-modal btn-cancel" 
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateFormData({
                    handle: '',
                    postUrlInstagram: '',
                    postUrlTikTok: '',
                    agreedPrice: '',
                    invoiceLink: '',
                    videoTopic: ''
                  });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-modal btn-create"
                onClick={createNewCollaboration}
                disabled={isCreating || !createFormData.handle || !createFormData.agreedPrice}
              >
                {isCreating ? 'Creating...' : 'Create Collaboration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Selection UI */}
      {selectedCollaborations.size > 0 && createPortal(
        <div 
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-primary, #ffffff)',
            borderRadius: '12px',
            padding: '12px 20px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            border: '1px solid var(--border-color, #e5e7eb)'
          }}
        >
          <span style={{ 
            color: 'var(--text-primary, #1f2937)', 
            fontWeight: '500',
            fontSize: '14px'
          }}>
            {selectedCollaborations.size} collaborations selected
          </span>
          
          <button
            onClick={fetchMetricsForSelected}
            style={{
              background: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#ffb300'}
            onMouseOut={(e) => e.currentTarget.style.background = '#ffc107'}
          >
            📊 Fetch Metrics
          </button>
          
          <button
            onClick={deleteSelectedCollaborations}
            style={{
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
            onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
          >
            🗑️ Delete
          </button>
          
          <button
            onClick={() => {
              setSelectedCollaborations(new Set());
              setSelectAllCollaborations(false);
            }}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: '6px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f9fafb)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ✖️ Clear
          </button>
        </div>,
        document.body
      )}

      {/* Script Checklist Modal removed: integrated into ScriptAnalysisPanel on detail page */}

      {/* Audience Edit Modal */}
      {showAudienceModal && selectedCollabForAudience && (
        <AudienceEditModal
          collaboration={selectedCollabForAudience}
          onClose={() => {
            setShowAudienceModal(false);
            setSelectedCollabForAudience(null);
          }}
          onSaved={async () => {
            const newCollaborations = await fetchCollaborations();
            if (newCollaborations && selectedCollabForAudience) {
              const updatedCollab = newCollaborations.find(
                c => c._id === selectedCollabForAudience._id
              );
              if (updatedCollab) {
                setSelectedCollabForAudience(updatedCollab);
              }
            }
            toast.success('Audience data updated successfully');
          }}
        />
      )}

      {/* Email Settings Modal */}
      <EmailSettingsModal
        isOpen={showEmailSettingsModal}
        onClose={() => setShowEmailSettingsModal(false)}
      />

    </div>
  );
};

export default Collaborations;
